import React, { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { api, ApiError } from '../utils/api';
import { tokenStore } from '../utils/tokenStore';

const AuthContext = createContext(null);

const ADMIN_ROLE = 'admin';

const initialState = {
  user: tokenStore.getUser(),
  isAuthenticated: !!tokenStore.getAccessToken(),
  loading: false,
  error: null,
  initializing: !!tokenStore.getAccessToken(), // verify token on boot
};

function reducer(state, action) {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true, error: null };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        loading: false,
        error: null,
        user: action.payload,
        isAuthenticated: true,
        initializing: false,
      };
    case 'LOGIN_FAILURE':
      return { ...state, loading: false, error: action.payload, isAuthenticated: false, user: null };
    case 'SET_USER':
      return { ...state, user: action.payload, isAuthenticated: !!action.payload, initializing: false };
    case 'LOGOUT':
      return { ...initialState, user: null, isAuthenticated: false, initializing: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    default:
      return state;
  }
}

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  // On mount: if we already have a token, validate it by hitting /auth/me.
  useEffect(() => {
    if (!tokenStore.getAccessToken()) {
      dispatch({ type: 'SET_USER', payload: null });
      return;
    }
    let cancelled = false;
    api
      .get('/auth/me')
      .then((user) => {
        if (cancelled) return;
        tokenStore.setUser(user);
        dispatch({ type: 'SET_USER', payload: user });
      })
      .catch(() => {
        if (cancelled) return;
        tokenStore.clear();
        dispatch({ type: 'LOGOUT' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Sync state across tabs and on forced-logout events from the API client.
  useEffect(() => {
    const onChange = () => {
      const user = tokenStore.getUser();
      const hasToken = !!tokenStore.getAccessToken();
      if (!hasToken) {
        dispatch({ type: 'LOGOUT' });
      } else {
        dispatch({ type: 'SET_USER', payload: user });
      }
    };
    window.addEventListener('auth:changed', onChange);
    window.addEventListener('auth:logout', onChange);
    return () => {
      window.removeEventListener('auth:changed', onChange);
      window.removeEventListener('auth:logout', onChange);
    };
  }, []);

  const login = useCallback(async ({ username, password }) => {
    dispatch({ type: 'LOGIN_START' });
    try {
      const data = await api.post('/auth/login', { username, password });
      tokenStore.set(data.accessToken, data.refreshToken, data.user);
      dispatch({ type: 'LOGIN_SUCCESS', payload: data.user });
      return data.user;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Login failed';
      dispatch({ type: 'LOGIN_FAILURE', payload: message });
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    const refreshToken = tokenStore.getRefreshToken();
    try {
      if (refreshToken) await api.post('/auth/logout', { refreshToken });
    } catch {
      // Always proceed with client-side logout even if the API call fails.
    }
    tokenStore.clear();
    dispatch({ type: 'LOGOUT' });
  }, []);

  const refreshProfile = useCallback(async () => {
    const user = await api.get('/auth/me');
    tokenStore.setUser(user);
    dispatch({ type: 'SET_USER', payload: user });
    return user;
  }, []);

  const hasPermission = useCallback(
    (...codes) => {
      if (!state.user) return false;
      if ((state.user.roles || []).includes(ADMIN_ROLE)) return true;
      const granted = new Set(state.user.permissions || []);
      return codes.some((c) => granted.has(c));
    },
    [state.user],
  );

  const hasRole = useCallback(
    (...roles) => {
      if (!state.user) return false;
      const has = new Set(state.user.roles || []);
      return roles.some((r) => has.has(r));
    },
    [state.user],
  );

  const clearError = useCallback(() => {
    dispatch({ type: 'CLEAR_ERROR' });
  }, []);

  const value = useMemo(
    () => ({
      ...state,
      login,
      logout,
      refreshProfile,
      hasPermission,
      hasRole,
      clearError,
    }),
    [state, login, logout, refreshProfile, hasPermission, hasRole, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
